import React, { useEffect, useRef, useState } from "react";
import {
  deleteInvoices,
  exportInvoices,
  getAllDistributors,
  getInvoices,
  getInvoicesBySku,
  getInvoiceSkuTypes,
  getLicenses,
  getSoftGenerateCount,
  softGenerateLicenses,
  updateInvoicesLicensesGenerated,
  updateInvoiceEntry,
  uploadInvoicesCsv,
  uploadInvoicesJson,
} from "@utils/api-communication/RestApiCli";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  Container,
  Divider,
  FileButton,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  ScrollArea,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useSelector } from "react-redux";
import {
  selectUserRole,
  selectUserDistributorId,
} from "@utils/redux-utils/selectors/userSelectors";
import { modals } from "@mantine/modals";
import { useLocation } from "react-router-dom";

export enum InvoiceSKU {
  HWP301 = "HWP301",
  HWS301 = "HWS301",
  ACCS01 = "ACCS01",
  SWVF11 = "SWVF11",
  LCDE00 = "LCDE00",
  LCUP11 = "LCUP11",
  FULL01 = "FULL01",
  SWMN01 = "SWMN01",
  FSSH01 = "FSSH01",
  FSHD01 = "FSHD01",
  ML01 = "ML01",
  ANS01 = "ANS01",
}

interface InvoicableItem {
  quantity: number;
  sku: InvoiceSKU;
}

interface SoftGenerateCountResult {
  distId: string;
  dryRun?: boolean;
  invoicesEvaluated: number;
  pendingInvoices: number;
  licensesPlanned: number;
  counts?: Record<string, number> | null;
  rawCounts?: Record<string, number> | null;
  // soft-generate (non-count) may return additional data
  [key: string]: any;
}

/** Modal body showing full dry-run response from the backend */
const DryRunResultModal: React.FC<{ result: SoftGenerateCountResult }> = ({ result }) => {
  const knownKeys = new Set([
    "distId",
    "dryRun",
    "invoicesEvaluated",
    "pendingInvoices",
    "licensesPlanned",
    "counts",
    "rawCounts",
  ]);
  const extraEntries = Object.entries(result).filter(([k]) => !knownKeys.has(k));

  return (
    <Stack gap="md">
      {/* Summary */}
      <Group gap="xl">
        <Stack gap={2} align="center">
          <Text size="xl" fw={700}>
            {result.invoicesEvaluated ?? "—"}
          </Text>
          <Text size="xs" c="dimmed">
            Invoices evaluated
          </Text>
        </Stack>
        <Stack gap={2} align="center">
          <Text size="xl" fw={700} c="orange">
            {result.pendingInvoices ?? "—"}
          </Text>
          <Text size="xs" c="dimmed">
            Pending
          </Text>
        </Stack>
        <Stack gap={2} align="center">
          <Text size="xl" fw={700} c="blue">
            {result.licensesPlanned ?? "—"}
          </Text>
          <Text size="xs" c="dimmed">
            Licenses planned
          </Text>
        </Stack>
      </Group>

      {/* Counts */}
      {result.counts && Object.keys(result.counts).length > 0 && (
        <>
          <Divider label="Counts by type" labelPosition="left" />
          <Table withRowBorders={false} verticalSpacing={4} horizontalSpacing="xs">
            <Table.Tbody>
              {Object.entries(result.counts).map(([key, val]) => (
                <Table.Tr key={key}>
                  <Table.Td>
                    <Text size="sm" style={{ textTransform: "capitalize" }}>
                      {key.replace(/([A-Z])/g, " $1")}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant={Number(val) > 0 ? "filled" : "outline"}
                      color={Number(val) > 0 ? "blue" : "gray"}
                    >
                      {val as number}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}

      {/* Raw counts */}
      {result.rawCounts && Object.keys(result.rawCounts).length > 0 && (
        <>
          <Divider label="Raw counts (by SKU)" labelPosition="left" />
          <Table withRowBorders={false} verticalSpacing={4} horizontalSpacing="xs">
            <Table.Tbody>
              {Object.entries(result.rawCounts).map(([sku, val]) => (
                <Table.Tr key={sku}>
                  <Table.Td>
                    <Code>{sku}</Code>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant={Number(val) > 0 ? "filled" : "outline"}
                      color={Number(val) > 0 ? "blue" : "gray"}
                    >
                      {val as number}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}

      {/* Any extra fields the backend returns */}
      {extraEntries.length > 0 && (
        <>
          <Divider label="Additional data" labelPosition="left" />
          <ScrollArea.Autosize mah={240}>
            <Code block style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
              {JSON.stringify(Object.fromEntries(extraEntries), null, 2)}
            </Code>
          </ScrollArea.Autosize>
        </>
      )}
    </Stack>
  );
};

interface Invoice {
  _id: string;
  invoiceNo: string;
  invoiceFrom?: any;
  invoiceTo?: any;
  items: InvoicableItem[];
  softLicensesGenerated?: boolean;
  date: string;
  distId: string;
  createdAt?: string;
  updatedAt?: string;
}

interface EditItemDraft {
  originalSku: InvoiceSKU;
  sku: InvoiceSKU;
  quantity: number;
  saving: boolean;
  error: string | null;
  saved: boolean;
}

interface InvoiceSkuTypeOption {
  sku: string;
  description: string;
}

interface ManualInvoiceRowDraft {
  id: string;
  date: string;
  desc: string;
  distId: string;
  invoiceNo: string;
}

const SKU_LABELS: Record<InvoiceSKU, string> = {
  [InvoiceSKU.HWP301]: "VR Headset – Pico Neo 3 Pro Eye",
  [InvoiceSKU.HWS301]: "VR Headset – Play for Dream S3",
  [InvoiceSKU.ACCS01]: "VR Headset Travel Case",
  [InvoiceSKU.SWVF11]: "Oculera Visual Field Analyzer Software v1.1",
  [InvoiceSKU.LCDE00]: "Demo Software Licence",
  [InvoiceSKU.LCUP11]: "Upgrade Licence (Demo → Full v1.1)",
  [InvoiceSKU.FULL01]: "Full Commercial License",
  [InvoiceSKU.SWMN01]: "Annual Software Maintenance Fee",
  [InvoiceSKU.FSSH01]: "Shipping Fee",
  [InvoiceSKU.FSHD01]: "Handling Fee",
  [InvoiceSKU.ML01]: "Monthly License",
  [InvoiceSKU.ANS01]: "Annual Subscription",
};

const SKU_OPTIONS = Object.values(InvoiceSKU).map((sku) => ({
  value: sku,
  label: `${sku} – ${SKU_LABELS[sku]}`,
}));

const getImportedInvoiceCount = (result: any): number | null => {
  if (typeof result?.success === "number") return result.success;
  if (Array.isArray(result?.created)) return result.created.length;
  if (typeof result?.created === "number") return result.created;
  return null;
};

const InvoicesList: React.FC = () => {
  const location = useLocation();
  const initialSkuTypeFilter = new URLSearchParams(location.search).get("skuType") ?? "";
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoiceNoFilter, setInvoiceNoFilter] = useState("");
  const [distIdFilter, setDistIdFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [skuTypeFilter, setSkuTypeFilter] = useState(initialSkuTypeFilter);
  const [licensesGeneratedFilter, setLicensesGeneratedFilter] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Edit modal state
  const [editInvoice, setEditInvoice] = useState<Invoice | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editDrafts, setEditDrafts] = useState<EditItemDraft[]>([]);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Export
  const [exporting, setExporting] = useState(false);

  // CSV import
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const manualInvoiceRowIdRef = useRef(0);
  const createEmptyManualInvoiceRow = (distId = ""): ManualInvoiceRowDraft => ({
    id: `manual-invoice-row-${manualInvoiceRowIdRef.current++}`,
    date: "",
    desc: "",
    distId,
    invoiceNo: "",
  });
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [manualInvoiceRows, setManualInvoiceRows] = useState<ManualInvoiceRowDraft[]>(() => [
    createEmptyManualInvoiceRow(),
  ]);

  // Per-row toggle state: id -> loading
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  // Bulk toggle
  const [bulkToggleValue, setBulkToggleValue] = useState<boolean | null>(null);
  const [bulkToggling, setBulkToggling] = useState(false);

  const [countData, setCountData] = useState<SoftGenerateCountResult | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  // Actual generation (dryRun: false)
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState<SoftGenerateCountResult | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [grantedLicenses, setGrantedLicenses] = useState<Record<string, number> | null>(null);
  const [grantedLoading, setGrantedLoading] = useState(false);
  const [distributorOptions, setDistributorOptions] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [invoiceSkuTypeOptions, setInvoiceSkuTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const role = useSelector(selectUserRole);
  const userDistId = useSelector(selectUserDistributorId);
  const isAdmin = role === "admin";

  const loadCounts = (distId: string) => {
    setCountLoading(true);
    getSoftGenerateCount(
      { distId, dryRun: true },
      (resp: any) => {
        setCountData(resp?.data ?? resp ?? null);
        setCountLoading(false);
      },
      (err: any) => {
        console.error(err);
        setCountLoading(false);
      },
    );
  };

  const loadGrantedLicenses = (distId: string) => {
    setGrantedLoading(true);
    const counts: Record<string, number> = {};

    const fetchPage = (page: number) => {
      getLicenses(
        { distId, page },
        (resp: any) => {
          const payload = resp?.data ?? resp ?? {};
          const items: any[] = payload.licenses ?? [];
          items.forEach((lic) => {
            const t = lic.type || "unknown";
            counts[t] = (counts[t] ?? 0) + 1;
          });
          const totalPages: number = payload.totalPages ?? 1;
          if (page < totalPages) {
            fetchPage(page + 1);
          } else {
            setGrantedLicenses(Object.keys(counts).length > 0 ? counts : null);
            setGrantedLoading(false);
          }
        },
        (err: any) => {
          console.error(err);
          setGrantedLoading(false);
        },
      );
    };

    fetchPage(1);
  };

  const handleGenerate = () => {
    const distId = isAdmin ? distIdFilter.trim() : (userDistId ?? "");
    if (!distId || !countData) return;

    modals.openConfirmModal({
      title: "Generate Licenses",
      children: (
        <Stack gap="xs">
          <Text size="sm">
            You are about to generate licenses for{" "}
            <Text span fw={700}>
              {countData.licensesPlanned}
            </Text>{" "}
            planned license{countData.licensesPlanned !== 1 ? "s" : ""} across{" "}
            <Text span fw={700}>
              {countData.pendingInvoices}
            </Text>{" "}
            pending invoice{countData.pendingInvoices !== 1 ? "s" : ""} for distributor{" "}
            <Text span fw={700}>
              {distId}
            </Text>
            .
          </Text>
          <Text size="sm" c="dimmed">
            This action cannot be undone.
          </Text>
        </Stack>
      ),
      labels: { confirm: "Generate", cancel: "Cancel" },
      confirmProps: { color: "green" },
      onConfirm: () => {
        setGenerating(true);
        setGenerateError(null);
        setGenerateResult(null);
        softGenerateLicenses(
          { distId },
          (resp: any) => {
            const result = resp?.data ?? resp ?? null;
            setGenerating(false);
            setGenerateResult(result);
            // Refresh preview counts, granted licenses, and invoice list
            loadCounts(distId);
            loadGrantedLicenses(distId);
            load();
          },
          (err: any) => {
            setGenerating(false);
            setGenerateError(err?.response?.data?.message || err?.message || "Generation failed");
          },
        );
      },
    });
  };

  const load = (overrides?: {
    invoiceNo?: string;
    date?: string;
    distId?: string;
    skuType?: string;
  }) => {
    setLoading(true);
    setError(null);
    setSelectedIds(new Set());

    const resolvedInvoiceNo = overrides?.invoiceNo ?? invoiceNoFilter.trim();
    const resolvedDate = overrides?.date ?? dateFilter.trim();
    const resolvedDistId = overrides?.distId ?? (isAdmin ? distIdFilter.trim() : "");
    const resolvedSkuType = overrides?.skuType ?? skuTypeFilter.trim();

    const handleSuccess = (resp: any) => {
      setInvoices(resp?.data || []);
      setLoading(false);
    };

    const handleError = (err: any) => {
      console.error(err);
      setError(err?.message || "Failed to fetch invoices");
      setLoading(false);
    };

    if (resolvedSkuType) {
      getInvoicesBySku(resolvedSkuType, handleSuccess, handleError);
      return;
    }

    const queryMap: { distId?: string; invoiceNo?: string; date?: string } = {};
    if (resolvedInvoiceNo) queryMap.invoiceNo = resolvedInvoiceNo;
    if (resolvedDate) queryMap.date = resolvedDate;
    if (isAdmin && resolvedDistId) queryMap.distId = resolvedDistId;

    getInvoices(queryMap, handleSuccess, handleError);
  };

  const handleExport = () => {
    setExporting(true);
    const queryMap: { distId?: string; invoiceNo?: string; date?: string } = {};
    if (invoiceNoFilter.trim()) queryMap.invoiceNo = invoiceNoFilter.trim();
    if (dateFilter.trim()) queryMap.date = dateFilter.trim();
    if (isAdmin && distIdFilter.trim()) queryMap.distId = distIdFilter.trim();

    exportInvoices(
      queryMap,
      (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        setExporting(false);
      },
      (err: any) => {
        console.error(err?.response?.data?.message || err?.message || "Export failed");
        setExporting(false);
      },
    );
  };

  const openUploadModal = () => {
    setUploadFile(null);
    setUploadResult(null);
    setUploadError(null);
    setManualInvoiceRows([createEmptyManualInvoiceRow(isAdmin ? "" : (userDistId ?? "").trim())]);
    setUploadModalOpen(true);
  };

  const refreshImportSummary = () => {
    const resolvedDistId = isAdmin ? distIdFilter.trim() : (userDistId ?? "").trim();
    if (!resolvedDistId) return;
    loadCounts(resolvedDistId);
    loadGrantedLicenses(resolvedDistId);
  };

  const closeUploadModal = () => {
    if (uploading) return;
    setUploadModalOpen(false);
  };

  const handleUpload = () => {
    if (!uploadFile) return;

    setUploading(true);
    setUploadError(null);
    setUploadResult(null);

    uploadInvoicesCsv(
      uploadFile,
      (resp: any) => {
        setUploading(false);
        setUploadResult(resp?.data ?? resp);
        load();
        refreshImportSummary();
      },
      (err: any) => {
        setUploading(false);
        setUploadError(err?.response?.data?.message || err?.message || "Upload failed");
      },
    );
  };

  const updateManualInvoiceRow = (
    rowId: string,
    patch: Partial<Omit<ManualInvoiceRowDraft, "id">>,
  ) => {
    setManualInvoiceRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
    );
  };

  const addManualInvoiceRow = () => {
    setManualInvoiceRows((prev) => [
      ...prev,
      createEmptyManualInvoiceRow(isAdmin ? "" : (userDistId ?? "").trim()),
    ]);
  };

  const removeManualInvoiceRow = (rowId: string) => {
    setManualInvoiceRows((prev) =>
      prev.length === 1 ? prev : prev.filter((row) => row.id !== rowId),
    );
  };

  const handleManualInvoiceImport = () => {
    const normalizedRows = manualInvoiceRows
      .map((row, index) => ({
        index,
        date: row.date.trim(),
        desc: row.desc.trim(),
        distId: row.distId.trim(),
        invoiceNo: row.invoiceNo.trim(),
      }))
      .filter((row) => row.date || row.desc || row.distId || row.invoiceNo);

    if (normalizedRows.length === 0) {
      setUploadError("Add at least one invoice row.");
      return;
    }

    const invalidRow = normalizedRows.find(
      (row) => !row.date || !row.desc || !row.distId || !row.invoiceNo,
    );
    if (invalidRow) {
      setUploadError(
        `Row ${invalidRow.index + 1}: date, distId, desc, and invoice number are required.`,
      );
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadResult(null);

    const payload = JSON.stringify({
      invoices: normalizedRows.map((row) => ({
        date: row.date,
        desc: row.desc,
        distId: row.distId,
        invoiceNo: row.invoiceNo,
      })),
    });
    const file = new File([payload], "invoices.json", { type: "application/json" });

    uploadInvoicesJson(
      file,
      (resp: any) => {
        setUploading(false);
        setUploadResult(resp?.data ?? resp);
        load();
        refreshImportSummary();
      },
      (err: any) => {
        setUploading(false);
        setUploadError(err?.response?.data?.message || err?.message || "Upload failed");
      },
    );
  };

  useEffect(() => {
    load();
    const resolvedDistId = isAdmin ? distIdFilter.trim() : (userDistId ?? "");
    if (resolvedDistId) {
      loadCounts(resolvedDistId);
      loadGrantedLicenses(resolvedDistId);
    }

    if (isAdmin) {
      getAllDistributors(
        (resp: any) => {
          const list: { distId: string; country: string[] }[] = resp?.data ?? resp ?? [];
          setDistributorOptions(
            list
              .filter((d) => d.distId)
              .map((d) => ({
                value: d.distId,
                label: d.country?.length ? `${d.distId} (${d.country.join(", ")})` : d.distId,
              }))
              .sort((a, b) => a.label.localeCompare(b.label)),
          );
        },
        (err: any) => console.error("Failed to load distributors", err),
      );
    }

    getInvoiceSkuTypes(
      (resp: any) => {
        const list: InvoiceSkuTypeOption[] = resp?.data ?? resp ?? [];
        setInvoiceSkuTypeOptions(
          list
            .filter((item) => item?.sku)
            .map((item) => ({
              value: item.sku,
              label: item.description ? `${item.sku} - ${item.description}` : item.sku,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        );
      },
      (err: any) => console.error("Failed to load invoice SKU types", err),
    );
  }, []);

  // Selection helpers
  const visibleInvoices = invoices.filter((inv) => {
    if (licensesGeneratedFilter === "yes") return inv.softLicensesGenerated === true;
    if (licensesGeneratedFilter === "no") return inv.softLicensesGenerated !== true;
    return true;
  });

  const allSelected =
    visibleInvoices.length > 0 && visibleInvoices.every((inv) => selectedIds.has(inv._id));
  const someSelected = visibleInvoices.some((inv) => selectedIds.has(inv._id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleInvoices.map((inv) => inv._id)));
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSingle = (inv: Invoice) => {
    const newValue = !inv.softLicensesGenerated;
    setTogglingIds((prev) => new Set(prev).add(inv._id));
    updateInvoicesLicensesGenerated(
      { ids: [inv._id], softLicensesGenerated: newValue, distId: inv.distId },
      () => {
        setInvoices((prev) =>
          prev.map((i) => (i._id === inv._id ? { ...i, softLicensesGenerated: newValue } : i)),
        );
        setTogglingIds((prev) => {
          const n = new Set(prev);
          n.delete(inv._id);
          return n;
        });
      },
      (err: any) => {
        console.error(err);
        setTogglingIds((prev) => {
          const n = new Set(prev);
          n.delete(inv._id);
          return n;
        });
      },
    );
  };

  const handleBulkToggle = (value: boolean) => {
    setBulkToggleValue(value);
    setBulkToggling(true);
    const selectedInvoices = invoices.filter((inv) => selectedIds.has(inv._id));
    const distId = selectedInvoices[0]?.distId;
    updateInvoicesLicensesGenerated(
      { ids: Array.from(selectedIds), softLicensesGenerated: value, distId },
      () => {
        setInvoices((prev) =>
          prev.map((i) => (selectedIds.has(i._id) ? { ...i, softLicensesGenerated: value } : i)),
        );
        setBulkToggling(false);
        setBulkToggleValue(null);
      },
      (err: any) => {
        console.error(err);
        setBulkToggling(false);
        setBulkToggleValue(null);
      },
    );
  };

  const doDeleteInvoices = () => {
    const selectedInvoices = invoices.filter((inv) => selectedIds.has(inv._id));
    const distId = selectedInvoices[0]?.distId;
    deleteInvoices(
      Array.from(selectedIds),
      () => {
        setSelectedIds(new Set());
        load();
      },
      (err: any) => {
        console.error(err?.response?.data?.message || err?.message || "Failed to delete invoices");
      },
      distId,
    );
  };

  const openDeleteConfirm = () => {
    const count = selectedIds.size;
    modals.openConfirmModal({
      title: "Delete Invoices",
      children: (
        <Text size="sm">
          You are about to permanently delete{" "}
          <Text span fw={700}>
            {count}
          </Text>{" "}
          invoice{count !== 1 ? "s" : ""}. This cannot be undone.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: doDeleteInvoices,
    });
  };

  const openModal = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedInvoice(null);
  };

  const openEditModal = (invoice: Invoice) => {
    setEditInvoice(invoice);
    setEditDrafts(
      (invoice.items ?? []).map((item) => ({
        originalSku: item.sku,
        sku: item.sku,
        quantity: item.quantity,
        saving: false,
        error: null,
        saved: false,
      })),
    );
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditInvoice(null);
    setEditDrafts([]);
    load();
  };

  const updateDraft = (idx: number, patch: Partial<EditItemDraft>) => {
    setEditDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };

  const saveEntry = (idx: number) => {
    if (!editInvoice) return;
    const draft = editDrafts[idx];
    updateDraft(idx, { saving: true, error: null, saved: false });

    // Only include sku in the body when it's actually being renamed;
    // always include quantity since that's the primary edit target.
    const body: { distId?: string; sku?: string; quantity?: number } = {
      quantity: draft.quantity,
    };
    if (draft.sku !== draft.originalSku) body.sku = draft.sku;

    // distId is required for admin requests (backend throws if absent/empty)
    if (isAdmin) {
      const distId = editInvoice.distId;
      if (!distId) {
        updateDraft(idx, { saving: false, error: "distId is missing on this invoice" });
        return;
      }
      body.distId = distId;
    }

    updateInvoiceEntry(
      editInvoice._id,
      draft.originalSku,
      body,
      () => updateDraft(idx, { saving: false, saved: true, originalSku: draft.sku }),
      (err: any) =>
        updateDraft(idx, {
          saving: false,
          error: err?.response?.data?.message || err?.message || "Failed to save",
        }),
    );
  };

  const formatAddress = (obj: any) => {
    if (!obj) return "-";
    if (typeof obj === "string") return obj;
    return (
      [obj.name, obj.address, obj.city, obj.country].filter(Boolean).join(", ") ||
      JSON.stringify(obj)
    );
  };

  const colSpan = isAdmin ? 7 : 6;

  return (
    <Container size="lg" mt="md">
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Title order={3}>Invoices</Title>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <TextInput
            placeholder="Invoice No."
            value={invoiceNoFilter}
            onChange={(e) => setInvoiceNoFilter(e.currentTarget.value)}
            style={{ minWidth: 160 }}
          />
          {isAdmin && (
            <Select
              placeholder="Distributor"
              data={distributorOptions}
              value={distIdFilter || null}
              onChange={(val) => setDistIdFilter(val ?? "")}
              clearable
              searchable
              style={{ minWidth: 220 }}
            />
          )}
          <TextInput
            placeholder="Date (YYYY-MM-DD)"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.currentTarget.value)}
            style={{ minWidth: 160 }}
          />
          <Select
            placeholder="SKU Type"
            data={invoiceSkuTypeOptions}
            value={skuTypeFilter || null}
            onChange={(val) => {
              const nextSku = val ?? "";
              setSkuTypeFilter(nextSku);
              setSelectedIds(new Set());
              load({ skuType: nextSku });
            }}
            clearable
            searchable
            style={{ minWidth: 240 }}
          />
          <Select
            placeholder="Licenses Generated"
            data={[
              { value: "yes", label: "Generated" },
              { value: "no", label: "Not Generated" },
            ]}
            value={licensesGeneratedFilter}
            onChange={(val) => {
              setLicensesGeneratedFilter(val);
              setSelectedIds(new Set());
            }}
            clearable
            style={{ minWidth: 170 }}
          />
          <Button
            onClick={() => {
              load();
              const resolvedDistId = isAdmin ? distIdFilter.trim() : (userDistId ?? "");
              if (resolvedDistId) {
                loadCounts(resolvedDistId);
                loadGrantedLicenses(resolvedDistId);
              }
            }}
          >
            Search
          </Button>
          <Button variant="light" loading={exporting} onClick={handleExport}>
            Export
          </Button>
          <Button variant="light" color="teal" onClick={openUploadModal}>
            Add Invoice
          </Button>
        </div>
      </div>

      {/* Bulk-action bar — only visible when rows are selected */}
      {someSelected && (
        <Group
          mt="sm"
          p="sm"
          style={{
            background: "var(--mantine-color-red-0)",
            border: "1px solid var(--mantine-color-red-3)",
            borderRadius: "var(--mantine-radius-md)",
          }}
          justify="space-between"
        >
          <Text size="sm" fw={500}>
            {selectedIds.size} invoice{selectedIds.size !== 1 ? "s" : ""} selected
          </Text>
          <Group gap={8}>
            <Button size="xs" variant="subtle" onClick={() => setSelectedIds(new Set())}>
              Clear selection
            </Button>
            <Tooltip label="Mark licenses as generated for selected invoices">
              <Button
                size="xs"
                color="green"
                variant="light"
                loading={bulkToggling && bulkToggleValue === true}
                disabled={bulkToggling}
                onClick={() => handleBulkToggle(true)}
              >
                Mark Generated
              </Button>
            </Tooltip>
            <Tooltip label="Mark licenses as NOT generated for selected invoices">
              <Button
                size="xs"
                color="orange"
                variant="light"
                loading={bulkToggling && bulkToggleValue === false}
                disabled={bulkToggling}
                onClick={() => handleBulkToggle(false)}
              >
                Mark Not Generated
              </Button>
            </Tooltip>
            <Button size="xs" color="red" onClick={openDeleteConfirm}>
              Delete selected
            </Button>
          </Group>
        </Group>
      )}

      {/* License counts card */}
      {(countData || countLoading || (!isAdmin && userDistId)) && (
        <Card withBorder mt="md" radius="md" padding="md">
          {/* ── Card header ── */}
          <Group justify="space-between" mb={8} wrap="nowrap">
            <div>
              <Title order={5}>License Counts</Title>
              {countData && (
                <Text size="xs" c="dimmed" mt={2}>
                  {countData.distId} · {countData.invoicesEvaluated} invoice
                  {countData.invoicesEvaluated !== 1 ? "s" : ""} evaluated ·{" "}
                  {countData.pendingInvoices} pending · {countData.licensesPlanned} planned
                </Text>
              )}
            </div>
            <Group gap={8} wrap="nowrap">
              <Tooltip label="Re-run dry run to refresh the preview">
                <Button
                  size="xs"
                  variant="light"
                  loading={countLoading}
                  disabled={generating}
                  onClick={() => {
                    const distId = isAdmin ? distIdFilter.trim() : (userDistId ?? "");
                    if (!distId) return;
                    setGenerating(true);
                    softGenerateLicenses(
                      { distId, dryRun: true },
                      (resp: any) => {
                        setGenerating(false);
                        const result = resp?.data ?? resp ?? {};
                        modals.open({
                          title: (
                            <Group gap="xs">
                              <Text fw={600}>Dry Run Result</Text>
                              <Badge variant="outline" color="blue" size="sm">
                                {result.distId}
                              </Badge>
                            </Group>
                          ),
                          size: "xl",
                          children: <DryRunResultModal result={result} />,
                        });
                      },
                      (err: any) => {
                        setGenerating(false);
                        setGenerateError(
                          err?.response?.data?.message || err?.message || "Dry run failed",
                        );
                      },
                    );
                  }}
                >
                  Dry Run
                </Button>
              </Tooltip>
              <Tooltip
                label={
                  !countData || countData.licensesPlanned === 0
                    ? "No licenses to generate"
                    : "Generate licenses for pending invoices"
                }
              >
                <Button
                  size="xs"
                  color="green"
                  loading={generating}
                  disabled={countLoading || !countData || countData.licensesPlanned === 0}
                  onClick={handleGenerate}
                >
                  Generate
                </Button>
              </Tooltip>
            </Group>
          </Group>

          <Divider mb="sm" />

          {/* ── Loading state ── */}
          {countLoading && (
            <Group gap={8}>
              <Loader size="xs" />
              <Text size="sm">Loading counts...</Text>
            </Group>
          )}

          {/* ── Dry-run preview ── */}
          {!countLoading && countData && (
            <Stack gap={6}>
              {Object.entries(countData.counts ?? {}).map(([key, value]) => (
                <Group key={key} justify="space-between" style={{ maxWidth: 280 }}>
                  <Text size="sm" style={{ textTransform: "capitalize" }}>
                    {key.replace(/([A-Z])/g, " $1")}
                  </Text>
                  <Badge
                    variant={value > 0 ? "filled" : "outline"}
                    color={value > 0 ? "blue" : "gray"}
                  >
                    {value}
                  </Badge>
                </Group>
              ))}
            </Stack>
          )}

          {!countLoading && !countData && isAdmin && !distIdFilter.trim() && (
            <Text size="sm" c="dimmed">
              Enter a Distributor ID to see license counts.
            </Text>
          )}

          {/* ── Generated Licenses ── */}
          {(grantedLicenses || grantedLoading) && (
            <>
              <Divider label="Generated Licenses" labelPosition="left" mt="sm" mb={6} />
              {grantedLoading ? (
                <Group gap={8}>
                  <Loader size="xs" />
                  <Text size="sm">Loading generated licenses...</Text>
                </Group>
              ) : grantedLicenses ? (
                <Stack gap={6}>
                  {Object.entries(grantedLicenses).map(([type, count]) => (
                    <Group key={type} justify="space-between" style={{ maxWidth: 280 }}>
                      <Text size="sm" style={{ textTransform: "capitalize" }}>
                        {type.replace(/-/g, " ")}
                      </Text>
                      <Badge variant="filled" color="teal">
                        {count}
                      </Badge>
                    </Group>
                  ))}
                </Stack>
              ) : null}
            </>
          )}

          {/* ── Generation error ── */}
          {generateError && (
            <Alert color="red" title="Generation failed" variant="light" mt="sm">
              {generateError}
            </Alert>
          )}

          {/* ── Generation success result ── */}
          {generateResult && !generating && (
            <Alert color="green" title="Licenses generated successfully" variant="light" mt="sm">
              <DryRunResultModal result={generateResult} />
            </Alert>
          )}
        </Card>
      )}

      {loading && (
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8 }}>
          <Loader />
          <Text>Loading...</Text>
        </div>
      )}

      {error && <Text style={{ color: "red", marginTop: 16 }}>{error}</Text>}

      {!loading && !error && (
        <Table mt="md" horizontalSpacing="md" verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 36 }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </Table.Th>
              <Table.Th>Invoice No.</Table.Th>
              {isAdmin && <Table.Th>Distributor</Table.Th>}
              <Table.Th>Date</Table.Th>
              <Table.Th>Items</Table.Th>
              <Table.Th>Licenses Generated</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {visibleInvoices.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={colSpan}>
                  <Text c="dimmed" size="sm">
                    {invoices.length > 0
                      ? "No invoices match the current filter."
                      : "No invoices found."}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {visibleInvoices.map((inv) => (
              <Table.Tr
                key={inv._id}
                bg={selectedIds.has(inv._id) ? "var(--mantine-color-red-0)" : undefined}
              >
                <Table.Td>
                  <Checkbox
                    checked={selectedIds.has(inv._id)}
                    onChange={() => toggleOne(inv._id)}
                    aria-label={`Select ${inv.invoiceNo}`}
                  />
                </Table.Td>
                <Table.Td>{inv.invoiceNo}</Table.Td>
                {isAdmin && <Table.Td>{inv.distId}</Table.Td>}
                <Table.Td>{inv.date ?? "-"}</Table.Td>
                <Table.Td>{inv.items?.length ?? 0}</Table.Td>
                <Table.Td>
                  <Tooltip
                    label={
                      inv.softLicensesGenerated
                        ? "Click to mark as not generated"
                        : "Click to mark as generated"
                    }
                  >
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      loading={togglingIds.has(inv._id)}
                      onClick={() => handleToggleSingle(inv)}
                      px={4}
                    >
                      <Badge
                        color={inv.softLicensesGenerated ? "green" : "gray"}
                        style={{ cursor: "pointer" }}
                      >
                        {inv.softLicensesGenerated ? "Yes" : "No"}
                      </Badge>
                    </Button>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Group gap={6}>
                    <Button size="xs" variant="default" onClick={() => openModal(inv)}>
                      View
                    </Button>
                    <Button size="xs" variant="light" onClick={() => openEditModal(inv)}>
                      Edit
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {/* ── View modal ── */}
      <Modal
        opened={modalOpen}
        onClose={closeModal}
        title={`Invoice ${selectedInvoice?.invoiceNo ?? ""}`}
        size="lg"
      >
        {selectedInvoice && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Table>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600, width: 180 }}>Invoice No.</Table.Td>
                  <Table.Td>{selectedInvoice.invoiceNo}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600 }}>Date</Table.Td>
                  <Table.Td>{selectedInvoice.date ?? "-"}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600 }}>Distributor</Table.Td>
                  <Table.Td>{selectedInvoice.distId ?? "-"}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600 }}>From</Table.Td>
                  <Table.Td>{formatAddress(selectedInvoice.invoiceFrom)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600 }}>To</Table.Td>
                  <Table.Td>{formatAddress(selectedInvoice.invoiceTo)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600 }}>Licenses Generated</Table.Td>
                  <Table.Td>
                    <Badge color={selectedInvoice.softLicensesGenerated ? "green" : "gray"}>
                      {selectedInvoice.softLicensesGenerated ? "Yes" : "No"}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600 }}>Created</Table.Td>
                  <Table.Td>
                    {selectedInvoice.createdAt
                      ? new Date(selectedInvoice.createdAt).toLocaleString()
                      : "-"}
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
            {!!selectedInvoice.items?.length && (
              <>
                <Title order={6} mt={4}>
                  Items ({selectedInvoice.items.length})
                </Title>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 40 }}>#</Table.Th>
                      <Table.Th>SKU</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th style={{ width: 100 }}>Quantity</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {selectedInvoice.items.map((item, idx) => (
                      <Table.Tr key={idx}>
                        <Table.Td>{idx + 1}</Table.Td>
                        <Table.Td>{item.sku}</Table.Td>
                        <Table.Td>{SKU_LABELS[item.sku] ?? item.sku}</Table.Td>
                        <Table.Td>{item.quantity}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            )}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <Button variant="default" onClick={closeModal}>
            Close
          </Button>
        </div>
      </Modal>

      {/* ── Edit modal ── */}
      <Modal
        opened={editModalOpen}
        onClose={closeEditModal}
        title={`Edit Invoice ${editInvoice?.invoiceNo ?? ""}`}
        size="xl"
      >
        {editInvoice && (
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              {editInvoice.distId} · {editInvoice.date}
            </Text>
            <Divider />
            {editDrafts.length === 0 && <Text size="sm">No items on this invoice.</Text>}
            {editDrafts.map((draft, idx) => (
              <Card key={idx} withBorder radius="sm" padding="sm">
                <Group align="flex-end" gap="sm" wrap="nowrap">
                  <Text size="xs" c="dimmed" style={{ width: 20, flexShrink: 0 }}>
                    {idx + 1}
                  </Text>
                  <Select
                    label="SKU"
                    data={SKU_OPTIONS}
                    value={draft.sku}
                    onChange={(val) =>
                      val && updateDraft(idx, { sku: val as InvoiceSKU, saved: false })
                    }
                    style={{ flex: 1, minWidth: 200 }}
                    searchable
                  />
                  <NumberInput
                    label="Quantity"
                    value={draft.quantity}
                    onChange={(val) =>
                      updateDraft(idx, { quantity: Number(val) || 0, saved: false })
                    }
                    min={0}
                    style={{ width: 100 }}
                  />
                  <Button
                    size="sm"
                    loading={draft.saving}
                    color={draft.saved ? "green" : "blue"}
                    onClick={() => saveEntry(idx)}
                    style={{ marginBottom: draft.error ? 20 : 0 }}
                  >
                    {draft.saved ? "Saved" : "Save"}
                  </Button>
                </Group>
                {draft.error && (
                  <Text size="xs" c="red" mt={4}>
                    {draft.error}
                  </Text>
                )}
              </Card>
            ))}
          </Stack>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <Button variant="default" onClick={closeEditModal}>
            Done
          </Button>
        </div>
      </Modal>

      {/* ── CSV Import modal ── */}
      <Modal opened={uploadModalOpen} onClose={closeUploadModal} title="Add Invoice" size="xl">
        <Stack gap="md">
          <Alert color="blue" title="Expected CSV format" variant="light">
            <Stack gap={4}>
              <Text size="sm">The CSV header must be exactly:</Text>
              <Code>date,distId,desc,invoiceNo</Code>
              <Text size="sm">
                <Text span fw={600}>
                  date
                </Text>
                : use a valid date, preferably <Code>YYYY-MM-DD</Code>
              </Text>
              <Text size="sm">
                <Text span fw={600}>
                  distId
                </Text>
                : distributor ID for that invoice
              </Text>
              <Text size="sm">
                <Text span fw={600}>
                  desc
                </Text>
                : one or more items in <Code>{`{quantity}x{SKU}`}</Code> format, separated by commas
              </Text>
              <Text size="sm">
                If <Code>desc</Code> contains multiple items, wrap it in quotes. Example:{" "}
                <Code>"2xSWVF11,1xANS01"</Code>
              </Text>
              <Text size="sm">
                <Text span fw={600}>
                  invoiceNo
                </Text>
                : invoice number
              </Text>
              <Text size="sm">Example row:</Text>
              <Code>2025-05-12,eyesoft,"4xML01,1xANS01",VIR-0002</Code>
            </Stack>
          </Alert>

          <Divider label="Import From CSV" labelPosition="left" />

          <div>
            <Text size="sm" fw={500} mb={6}>
              CSV file
            </Text>
            <Paper
              withBorder
              p="md"
              radius="md"
              style={{
                borderStyle: "dashed",
                borderColor: uploadFile
                  ? "var(--mantine-color-teal-5)"
                  : "var(--mantine-color-gray-4)",
                background: uploadFile
                  ? "var(--mantine-color-teal-0)"
                  : "var(--oculera-surface-alt)",
                transition: "all 150ms ease",
              }}
            >
              <Stack align="center" gap={8}>
                {uploadFile ? (
                  <>
                    <Text size="sm" fw={600} c="teal">
                      {uploadFile.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {(uploadFile.size / 1024).toFixed(1)} KB
                    </Text>
                  </>
                ) : (
                  <Text size="sm" c="dimmed">
                    No file selected
                  </Text>
                )}
                <FileButton onChange={setUploadFile} accept=".csv,text/csv">
                  {(props) => (
                    <Button
                      {...props}
                      variant="light"
                      color="teal"
                      size="xs"
                      disabled={uploading || !!uploadResult}
                    >
                      {uploadFile ? "Change file" : "Choose CSV file"}
                    </Button>
                  )}
                </FileButton>
              </Stack>
            </Paper>
          </div>

          <Group justify="flex-end">
            <Button
              color="teal"
              loading={uploading}
              disabled={!uploadFile || !!uploadResult}
              onClick={handleUpload}
            >
              Upload CSV
            </Button>
          </Group>

          <Divider label="Add Rows Manually" labelPosition="left" />

          <Text size="sm" c="dimmed">
            Add invoice rows using the same fields as the CSV import. The <Code>desc</Code> value
            must use the same format, such as <Code>"2xSWVF11,1xANS01"</Code>.
          </Text>

          <Stack gap="sm">
            {manualInvoiceRows.map((row, index) => (
              <Card key={row.id} withBorder radius="sm" padding="md">
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <Text fw={600}>Row {index + 1}</Text>
                    <Button
                      variant="subtle"
                      color="red"
                      size="xs"
                      disabled={uploading || !!uploadResult || manualInvoiceRows.length === 1}
                      onClick={() => removeManualInvoiceRow(row.id)}
                    >
                      Remove
                    </Button>
                  </Group>
                  <Group align="flex-start" grow>
                    <TextInput
                      label="Date"
                      placeholder="YYYY-MM-DD"
                      value={row.date}
                      disabled={uploading || !!uploadResult}
                      onChange={(event) =>
                        updateManualInvoiceRow(row.id, { date: event.currentTarget.value })
                      }
                    />
                    <TextInput
                      label="Distributor ID"
                      placeholder="eyesoft"
                      value={row.distId}
                      disabled={uploading || !!uploadResult || !isAdmin}
                      onChange={(event) =>
                        updateManualInvoiceRow(row.id, { distId: event.currentTarget.value })
                      }
                    />
                    <TextInput
                      label="Invoice No."
                      placeholder="VIR-0002"
                      value={row.invoiceNo}
                      disabled={uploading || !!uploadResult}
                      onChange={(event) =>
                        updateManualInvoiceRow(row.id, { invoiceNo: event.currentTarget.value })
                      }
                    />
                  </Group>
                  <Textarea
                    label="Description"
                    placeholder="4xML01,1xANS01"
                    autosize
                    minRows={2}
                    value={row.desc}
                    disabled={uploading || !!uploadResult}
                    description='Use comma-separated item tokens in the form "{quantity}x{SKU}".'
                    onChange={(event) =>
                      updateManualInvoiceRow(row.id, { desc: event.currentTarget.value })
                    }
                  />
                </Stack>
              </Card>
            ))}
          </Stack>

          <Group justify="space-between">
            <Button
              variant="default"
              onClick={addManualInvoiceRow}
              disabled={uploading || !!uploadResult}
            >
              Add row
            </Button>
            <Button
              color="blue"
              loading={uploading}
              disabled={!!uploadResult}
              onClick={handleManualInvoiceImport}
            >
              Submit Rows
            </Button>
          </Group>

          {uploadError && (
            <Alert color="red" title="Upload failed" variant="light">
              {uploadError}
            </Alert>
          )}

          {uploadResult && (
            <Alert color="teal" title="Import complete" variant="light">
              <Stack gap={4}>
                {getImportedInvoiceCount(uploadResult) != null && (
                  <Text size="sm">
                    <Text span fw={600}>
                      {getImportedInvoiceCount(uploadResult)}
                    </Text>{" "}
                    invoice
                    {getImportedInvoiceCount(uploadResult) !== 1 ? "s" : ""} imported
                  </Text>
                )}
                {uploadResult.updated != null && (
                  <Text size="sm">
                    <Text span fw={600}>
                      {uploadResult.updated}
                    </Text>{" "}
                    invoice{uploadResult.updated !== 1 ? "s" : ""} updated
                  </Text>
                )}
                {uploadResult.failed != null && uploadResult.failed > 0 && (
                  <Text size="sm" c="orange">
                    <Text span fw={600}>
                      {uploadResult.failed}
                    </Text>{" "}
                    row{uploadResult.failed !== 1 ? "s" : ""} failed
                  </Text>
                )}
                {uploadResult.skipped != null && uploadResult.skipped > 0 && (
                  <Text size="sm" c="orange">
                    <Text span fw={600}>
                      {uploadResult.skipped}
                    </Text>{" "}
                    row{uploadResult.skipped !== 1 ? "s" : ""} skipped
                  </Text>
                )}
                {Array.isArray(uploadResult.errors) && uploadResult.errors.length > 0 && (
                  <>
                    <Text size="sm" c="red" fw={500} mt={4}>
                      Errors ({uploadResult.errors.length}):
                    </Text>
                    <ScrollArea mah={120}>
                      <Stack gap={2}>
                        {uploadResult.errors.map((e: any, i: number) => (
                          <Code key={i} block style={{ fontSize: 11 }}>
                            {typeof e === "string"
                              ? e
                              : `${e?.line ? `Line ${e.line}: ` : ""}${e?.error ?? JSON.stringify(e)}`}
                          </Code>
                        ))}
                      </Stack>
                    </ScrollArea>
                  </>
                )}
              </Stack>
            </Alert>
          )}

          <Group justify="flex-end" gap={8}>
            <Button variant="default" onClick={closeUploadModal} disabled={uploading}>
              {uploadResult ? "Close" : "Cancel"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default InvoicesList;
